// /pages/HR/utils/hrDataService.js
import { mockHRData } from './mockData';
import apiClient from '../../../../apiClient';
import { HR_LOCATIONS } from './hrConstants';

/**
 * HR Data Service
 * Handles all data fetching and processing for HR Dashboard
 */

// Set to false to use real API, true for mock data
const USE_MOCK_DATA = process.env.REACT_APP_USE_MOCK_DATA === 'true' || false;

/**
 * Build query parameters from filters object
 * @param {Object} filters - Filter object from FilterBar
 * @returns {string} - Query string
 */
function buildQueryParams(filters = {}) {
  const params = new URLSearchParams();

  // Created date range
  if (filters.createdDateRange?.startDate) {
    params.append('created_start', filters.createdDateRange.startDate.toISOString());
  }
  if (filters.createdDateRange?.endDate) {
    params.append('created_end', filters.createdDateRange.endDate.toISOString());
  }

  // Resolved date range
  if (filters.resolvedDateRange?.startDate) {
    params.append('resolved_start', filters.resolvedDateRange.startDate.toISOString());
  }
  if (filters.resolvedDateRange?.endDate) {
    params.append('resolved_end', filters.resolvedDateRange.endDate.toISOString());
  }

  // Markets (locations)
  if (filters.locations && filters.locations.length > 0) {
    params.append('markets', filters.locations.join(','));
  }

  // Categories (IDs)
  if (filters.categories && filters.categories.length > 0) {
    params.append('categories', filters.categories.join(','));
  }

  // Status
  if (filters.status && filters.status !== 'all') {
    params.append('status', filters.status);
  }

  return params.toString();
}

class HRDataService {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes cache
  }

  /**
   * Generic fetch wrapper with error handling
   */
  async fetchData(endpoint, options = {}) {
    if (USE_MOCK_DATA) {
      return this.getMockData(endpoint);
    }

    try {
      const response = await apiClient.get(`/api${endpoint}`);
      // API returns { success: true, data: {...} }
      if (response.data && response.data.success) {
        return response.data.data;
      }
      return response.data;
    } catch (error) {
      console.error('API fetch error:', error);
      throw error;
    }
  }

  /**
   * Get mock data based on endpoint
   */
  getMockData(endpoint) {
    return new Promise((resolve) => {
      setTimeout(() => {
        if (endpoint.includes('kpis')) {
          resolve(mockHRData.kpis);
        } else if (endpoint.includes('locations')) {
          resolve(mockHRData.locationMetrics);
        } else if (endpoint.includes('categories')) {
          resolve(mockHRData.categoryBreakdown);
        } else if (endpoint.includes('weekly')) {
          resolve(mockHRData.weeklyData);
        } else if (endpoint.includes('market')) {
          resolve(mockHRData.marketDistribution);
        } else if (endpoint.includes('generalist')) {
          resolve(mockHRData.generalistData);
        } else {
          resolve(mockHRData);
        }
      }, 300 + Math.random() * 200);
    });
  }

  /**
   * Fetch main KPIs
   */
  async fetchKPIs(filters = {}) {
    const cacheKey = `kpis_${JSON.stringify(filters)}`;

    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.cacheTimeout) {
        return cached.data;
      }
    }

    const queryParams = buildQueryParams(filters);
    const endpoint = `/hr/kpis${queryParams ? `?${queryParams}` : ''}`;

    const data = await this.fetchData(endpoint);

    this.cache.set(cacheKey, {
      data,
      timestamp: Date.now(),
    });

    return data;
  }

  /**
   * Fetch location metrics
   */
  async fetchLocationMetrics(filters = {}) {
    const queryParams = buildQueryParams(filters);
    const endpoint = `/hr/locations${queryParams ? `?${queryParams}` : ''}`;
    const data = await this.fetchData(endpoint);

    // Transform API response to frontend format
    // API returns: { market, open, completed }
    // Frontend expects: { id, name, open, completed, icon }
    if (Array.isArray(data)) {
      return data.map(item => {
        const locationInfo = HR_LOCATIONS.find(loc => loc.code === item.market) ||
          { code: item.market || 'XX', name: 'Other', icon: '📍' };
        return {
          id: locationInfo.code,
          name: locationInfo.name,
          open: item.open || 0,
          completed: item.completed || 0,
          icon: locationInfo.icon
        };
      });
    }
    return data;
  }

  /**
   * Fetch category breakdown
   */
  async fetchCategoryBreakdown(filters = {}) {
    const queryParams = buildQueryParams(filters);
    const endpoint = `/hr/categories${queryParams ? `?${queryParams}` : ''}`;
    return this.fetchData(endpoint);
  }

  /**
   * Fetch weekly trend data
   */
  async fetchWeeklyData(filters = {}, weeks = 14) {
    const queryParams = buildQueryParams(filters);
    const weeksParam = queryParams ? `&weeks=${weeks}` : `?weeks=${weeks}`;
    const endpoint = `/hr/weekly${queryParams ? `?${queryParams}` : ''}${weeksParam}`;
    return this.fetchData(endpoint);
  }

  /**
   * Fetch generalist performance data
   */
  async fetchGeneralistData(filters = {}) {
    const queryParams = buildQueryParams(filters);
    const endpoint = `/hr/generalists${queryParams ? `?${queryParams}` : ''}`;
    return this.fetchData(endpoint);
  }

  /**
   * Fetch market distribution
   */
  async fetchMarketDistribution(filters = {}) {
    const queryParams = buildQueryParams(filters);
    const endpoint = `/hr/market-distribution${queryParams ? `?${queryParams}` : ''}`;
    const data = await this.fetchData(endpoint);

    // Transform API response to include market name
    if (Array.isArray(data)) {
      return data.map(item => {
        const locationInfo = HR_LOCATIONS.find(loc => loc.code === item.market) ||
          { code: item.market || 'XX', name: 'Other' };
        return {
          market: locationInfo.code,
          name: locationInfo.name,
          count: item.count || 0,
          percentage: item.percentage || 0
        };
      });
    }
    return data;
  }

  /**
   * Fetch complete dashboard data
   */
  async fetchDashboardData(filters = {}) {
    try {
      const [kpis, locations, categories, weekly, generalists, market] = await Promise.all([
        this.fetchKPIs(filters),
        this.fetchLocationMetrics(filters),
        this.fetchCategoryBreakdown(filters),
        this.fetchWeeklyData(filters),
        this.fetchGeneralistData(filters),
        this.fetchMarketDistribution(filters),
      ]);

      return {
        kpis,
        locationMetrics: locations,
        categoryBreakdown: categories,
        weeklyData: weekly,
        generalistData: generalists,
        marketDistribution: market,
        lastUpdated: new Date().toISOString(),
      };
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      console.error('API connection failed - make sure backend server is running with HR routes loaded');
      throw error;
    }
  }

  /**
   * Fetch filter options (categories, markets, statuses)
   */
  async fetchFilterOptions() {
    const endpoint = '/hr/filter-options';
    return this.fetchData(endpoint);
  }

  /**
   * Export dashboard data
   */
  async exportDashboardData(format, data) {
    const endpoint = '/hr/export';

    return this.fetchData(endpoint, {
      method: 'POST',
      body: JSON.stringify({
        format,
        data,
      }),
    });
  }

  /**
   * Process data for charts
   */
  processChartData(rawData, chartType) {
    if (!rawData || !Array.isArray(rawData)) {
      return { labels: [], data: [] };
    }

    switch (chartType) {
      case 'category':
        return {
          labels: rawData.map(item => item.category),
          data: rawData.map(item => item.count),
        };

      case 'weekly':
        return {
          labels: rawData.map(item => item.week),
          barData: rawData.map(item => item.created),
          lineData: rawData.map(item => item.avgDaysOpen),
        };

      case 'generalist':
        return {
          labels: rawData.map(item => item.name),
          data: rawData.map(item => item.completed),
        };

      case 'market':
        return {
          labels: rawData.map(item => item.market),
          data: rawData.map(item => item.count),
          percentages: rawData.map(item => item.percentage),
        };

      default:
        return rawData;
    }
  }

  /**
   * Calculate trends
   */
  calculateTrends(currentData, previousData) {
    const trends = {};

    for (const key in currentData) {
      if (typeof currentData[key] === 'number' && previousData[key]) {
        const current = currentData[key];
        const previous = previousData[key];
        const change = ((current - previous) / previous) * 100;
        trends[key] = {
          value: current,
          change: change.toFixed(1),
          direction: change > 0 ? 'up' : change < 0 ? 'down' : 'stable',
        };
      }
    }

    return trends;
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Save filter preferences
   */
  saveFilterPreferences(filters) {
    localStorage.setItem('hrDashboard_filters', JSON.stringify(filters));
  }

  /**
   * Load filter preferences
   */
  loadFilterPreferences() {
    const saved = localStorage.getItem('hrDashboard_filters');
    return saved ? JSON.parse(saved) : null;
  }
}

// Export singleton instance
export const hrDataService = new HRDataService();
